import React, { useEffect, useState, useRef } from 'react';
import { Snackbar, Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem, } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { Meal, mealsAPI, getImageUrl } from '../../services/api';
import { formatPersianNumber, toPersianNumber, toEnglishNumber } from '../../utils/persianNumber';

const AdminMeals: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);
  const [currentTab, setCurrentTab] = useState<'food' | 'salad' | 'breakfast' | 'beverage' | 'other'>('food');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    staffShare: '',
    imageUrl: '',
    ingredients: '',
    calories: '',
    category: 'food' as 'food' | 'salad' | 'breakfast' | 'beverage' | 'other',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadMeals();
  }, []);

  const loadMeals = async () => {
    try {
      setLoading(true);
      const data = await mealsAPI.getMeals();
      setMeals(data);
    } catch (err: any) {
      setError('خطا در بارگذاری غذاها');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (meal: Meal) => {
    setEditingMeal(meal);
    setFormData({
      title: meal.title,
      description: meal.description || '',
      price: meal.price?.toString() || '',
      staffShare: meal.staffShare?.toString() || '',
      imageUrl: meal.imageUrl || '',
      ingredients: meal.ingredients?.join(', ') || '',
      calories: meal.calories?.toString() || '',
      category: meal.category || 'food',
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMeal(null);
    setFormData({
      title: '',
      description: '',
      price: '',
      staffShare: '',
      imageUrl: '',
      ingredients: '',
      calories: '',
      category: currentTab,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const ingredientsArray = formData.ingredients
        ? formData.ingredients.split(',').map(ing => ing.trim()).filter(ing => ing.length > 0)
        : undefined;

      if (editingMeal) {
        await mealsAPI.updateMeal(editingMeal.id, {
          title: formData.title,
          description: formData.description || undefined,
          price: parseFloat(formData.price),
          staffShare: parseFloat(formData.staffShare),
          imageUrl: formData.imageUrl || undefined,
          ingredients: ingredientsArray,
          calories: formData.calories ? parseInt(formData.calories) : 0,
          category: formData.category,
        });
        setSuccess('آیتم با موفقیت به‌روزرسانی شد');
      } else {
        await mealsAPI.createMeal({
          title: formData.title,
          description: formData.description || undefined,
          price: parseFloat(formData.price),
          staffShare: parseFloat(formData.staffShare),
          imageUrl: formData.imageUrl || undefined,
          ingredients: ingredientsArray,
          calories: formData.calories ? parseInt(formData.calories) : 0,
          category: formData.category,
        });
        setSuccess('آیتم با موفقیت اضافه شد');
      }

      handleCloseModal();
      loadMeals();
    } catch (err: any) {
      setError(editingMeal ? 'خطا در به‌روزرسانی آیتم' : 'خطا در اضافه کردن آیتم');
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingImage(true);
        setError('');
        const mealId = editingMeal?.id || undefined;
        const imageUrl = await mealsAPI.uploadImage(file, mealId);
        setFormData({ ...formData, imageUrl });
        setSuccess('عکس با موفقیت آپلود شد');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err: any) {
        setError('خطا در آپلود عکس');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = async (mealId: string) => {
    if (!window.confirm('آیا از حذف این آیتم مطمئن هستید؟')) return;

    try {
      await mealsAPI.deleteMeal(mealId);
      setSuccess('آیتم با موفقیت حذف شد');
      loadMeals();
    } catch (err: any) {
      setError('خطا در حذف آیتم');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 600,
              mb: 1,
            }}
          >
            مدیریت آیتم‌های غذایی
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'Vazir, sans-serif' }}
          >
            تعریف و مدیریت موارد قابل فروش در منو
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingMeal(null);
            setFormData({
              title: '',
              description: '',
              price: '',
              staffShare: '',
              imageUrl: '',
              ingredients: '',
              calories: '',
              category: currentTab,
            });
            setOpenModal(true);
          }}
          sx={{ fontFamily: 'Vazir, sans-serif' }}
        >
          افزودن آیتم
        </Button>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            '& .MuiTab-root': { fontFamily: 'Vazir, sans-serif', fontWeight: 600 }
          }}
        >
          <Tab label="غذاها" value="food" />
          <Tab label="پیش غذا و سالاد" value="salad" />
          <Tab label="صبحانه و میان وعده" value="breakfast" />
          <Tab label="نوشیدنی ها" value="beverage" />
          <Tab label="سایر اقلام" value="other" />
        </Tabs>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عکس
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عنوان
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  قیمت
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  سهم پرسنل
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  امتیاز
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  کالری
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  مواد تشکیل دهنده
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عملیات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {meals.filter(meal => (meal.category || 'food') === currentTab).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'Vazir, sans-serif' }}
                    >
                      هیچ موردی در این دسته‌بندی یافت نشد
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                meals.filter(meal => (meal.category || 'food') === currentTab).map((meal) => (
                  <TableRow key={meal.id} hover>
                    <TableCell>
                      {meal.imageUrl ? (
                        <Box
                          component="img"
                          src={getImageUrl(meal.imageUrl)}
                          alt={meal.title}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: 'contain',
                            borderRadius: 1,
                            bgcolor: 'action.disabledBackground',
                          }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: 'action.disabledBackground',
                          }}
                        >
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {meal.title}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {formatPersianNumber(meal.price)} تومان
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {formatPersianNumber(meal.staffShare)} تومان
                    </TableCell>
                    <TableCell>
                      {meal.averageRating != null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Vazir, sans-serif',
                              fontWeight: 600,
                              color: 'warning.main',
                            }}
                          >
                            {toPersianNumber(meal.averageRating.toFixed(1))}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Box
                                key={star}
                                component="span"
                                sx={{
                                  color: star <= Math.round(meal.averageRating!) ? 'warning.main' : 'action.disabled',
                                  fontSize: '0.875rem',
                                }}
                              >
                                ★
                              </Box>
                            ))}
                          </Box>
                          {(meal.ratingCount ?? 0) > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.7rem' }}
                            >
                              ({toPersianNumber(meal.ratingCount ?? 0)})
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'Vazir, sans-serif' }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {meal.calories || '-'}
                    </TableCell>
                    <TableCell>
                      {meal.ingredients && meal.ingredients.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {meal.ingredients.slice(0, 3).map((ing, idx) => (
                            <Chip
                              key={idx}
                              label={ing}
                              size="small"
                              sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                            />
                          ))}
                          {meal.ingredients.length > 3 && (
                            <Chip
                              label={`+${meal.ingredients.length - 3}`}
                              size="small"
                              sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'Vazir, sans-serif' }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(meal)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(meal.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, textAlign: 'center' }}>
          {editingMeal ? 'ویرایش آیتم' : 'افزودن آیتم'}
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ px: 3, py: 2 }}>
            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Image Upload - Circular at top */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              {formData.imageUrl ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    src={getImageUrl(formData.imageUrl)}
                    alt="Preview"
                    sx={{
                      width: 120,
                      height: 120,
                      cursor: 'pointer',
                      border: '3px solid',
                      borderColor: 'primary.main',
                      mb: 1,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    onClick={handleImageClick}
                  />
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, imageUrl: '' });
                    }}
                    sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                  >
                    حذف عکس
                  </Button>
                </Box>
              ) : (
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'action.disabledBackground',
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={handleImageClick}
                >
                  <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Avatar>
              )}
            </Box>

            {/* Form Fields in Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="نام آیتم"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="مثال: کباب کوبیده"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>دسته‌بندی</InputLabel>
                <Select
                  value={formData.category}
                  label="دسته‌بندی"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                  sx={{ fontFamily: 'Vazir, sans-serif' }}
                >
                  <MenuItem value="food" sx={{ fontFamily: 'Vazir, sans-serif' }}>غذاها</MenuItem>
                  <MenuItem value="salad" sx={{ fontFamily: 'Vazir, sans-serif' }}>پیش غذا و سالاد</MenuItem>
                  <MenuItem value="breakfast" sx={{ fontFamily: 'Vazir, sans-serif' }}>صبحانه و میان وعده</MenuItem>
                  <MenuItem value="beverage" sx={{ fontFamily: 'Vazir, sans-serif' }}>نوشیدنی ها</MenuItem>
                  <MenuItem value="other" sx={{ fontFamily: 'Vazir, sans-serif' }}>سایر اقلام</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="قیمت (تومان)"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: toEnglishNumber(e.target.value) })}
                required
                placeholder="مثال: 50000"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                  min: 0,
                  inputMode: 'numeric'
                }}
              />
              <TextField
                fullWidth
                label="قیمت سهم پرسنل (تومان)"
                value={formData.staffShare}
                onChange={(e) => setFormData({ ...formData, staffShare: toEnglishNumber(e.target.value) })}
                required
                placeholder="مثال: 20000"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                  min: 0,
                  inputMode: 'numeric'
                }}
              />
              <TextField
                fullWidth
                label="کالری"
                value={formData.calories}
                onChange={(e) => setFormData({ ...formData, calories: toEnglishNumber(e.target.value) })}
                placeholder="مثال: 500"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                  min: 0,
                  inputMode: 'numeric'
                }}
              />
              <TextField
                fullWidth
                label="مواد تشکیل دهنده"
                placeholder="برنج، مرغ، سبزیجات"
                value={formData.ingredients}
                onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                helperText="با کاما جدا کنید"
                FormHelperTextProps={{
                  sx: { fontFamily: 'Vazir, sans-serif', fontSize: '0.7rem' },
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="توضیحات"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="توضیحات اضافی..."
              sx={{ mt: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            {uploadingImage && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'Vazir, sans-serif', display: 'block', mt: 1 }}
                >
                  در حال آپلود...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseModal}
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              {editingMeal ? 'به‌روزرسانی' : 'ذخیره'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

    </Box>
  );
};

export default AdminMeals;
